<app-report-header
  crumbLabel="Stamp Duty Calculator"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-header>

<div class="rpt-page" id="contentToConvert">

  <!-- ── Masthead ── -->
  <div class="rpt-wrap rpt-masthead">
    <div class="rpt-masthead__eyebrow">Stamp Duty Land Tax Report</div>
    <h1 class="rpt-masthead__title">SDLT Calculation — {{purchaseTypeLabel}}</h1>
    <p class="rpt-masthead__subtitle">
      Your Stamp Duty Land Tax liability based on the property value, purchase type, and buyer status entered.
    </p>
    <div class="rpt-masthead__meta">
      <span class="rpt-meta-pill">Property Value <strong>£{{calcData?.PropertyValue | number:'1.0-0'}}</strong></span>
      <span class="rpt-meta-pill">Type <strong>{{purchaseTypeLabel}}</strong></span>
      <span class="rpt-meta-pill" *ngIf="calcData?.ForeignBuyer==='1'">Foreign Buyer <strong>+2% surcharge</strong></span>
      <span class="rpt-meta-pill rpt-meta-pill--accent">Effective Rate <strong>{{effectiveRate}}%</strong></span>
    </div>
  </div>

  <!-- ── §01 KPI Section ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">01 · Results</div>
        <h2 class="rpt-section__title">Your SDLT liability</h2>
        <p class="rpt-section__sub">Total Stamp Duty Land Tax payable on your property purchase.</p>
      </div>
    </div>

    <div class="rpt-kpi-strip">
      <!-- Hero: Total SDLT -->
      <div class="rpt-kpi-hero">
        <div class="rpt-kpi-hero__eyebrow">Total SDLT Payable</div>
        <div class="rpt-kpi-hero__body">
          <div class="rpt-kpi-hero__value">£{{stampDutyLandTax | number:'1.0-0'}}</div>
          <div class="rpt-kpi-hero__label">{{purchaseTypeLabel}}</div>
        </div>
        <p class="rpt-kpi-hero__caption">
          Stamp Duty Land Tax due on a £{{calcData?.PropertyValue | number:'1.0-0'}} property
          <ng-container *ngIf="calcData?.PurchaseType==='investment'"> incl. 5% additional dwelling surcharge</ng-container><ng-container *ngIf="calcData?.ForeignBuyer==='1'">, plus 2% non-resident surcharge</ng-container>.
        </p>
      </div>

      <!-- Side: Effective Rate -->
      <div class="rpt-kpi-side rpt-kpi-side--1">
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Effective Tax Rate</div>
          <div class="rpt-kpi-card__value">{{effectiveRate}}<sup>%</sup></div>
          <div class="rpt-kpi-card__foot">On £{{calcData?.PropertyValue | number:'1.0-0'}} property value</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §02 Assumptions ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">02 · Inputs</div>
        <h2 class="rpt-section__title">Your assumptions</h2>
        <p class="rpt-section__sub">All values used in this calculation.</p>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-card__body">
        <div class="rpt-arow">
          <span class="rpt-arow__key">Property Value</span>
          <span class="rpt-arow__val">£{{calcData?.PropertyValue | number:'1.0-0'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Purchase Type</span>
          <span class="rpt-arow__val">{{purchaseTypeLabel}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Residency</span>
          <span class="rpt-arow__val">{{calcData?.ForeignBuyer==='1' ? 'Foreign Buyer (+2% surcharge)' : 'UK Resident'}}</span>
        </div>
        <div class="rpt-arow">
          <span class="rpt-arow__key">Effective Tax Rate</span>
          <span class="rpt-arow__val rpt-arow__val--accent">{{effectiveRate}}%</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §03 Band Breakdown ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">03 · Breakdown</div>
        <h2 class="rpt-section__title">Band-by-band breakdown</h2>
        <p class="rpt-section__sub">How the tax is calculated across each SDLT threshold band.</p>
      </div>
    </div>
    <div class="rpt-card">
      <div class="rpt-tbl-wrap">
        <table class="rpt-tbl">
          <thead>
            <tr>
              <th>Band</th>
              <th>Rate</th>
              <th>Taxable Amount</th>
              <th style="text-align:right">Tax Due</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let b of bandBreakdown">
              <td class="rpt-tbl__key">{{b.band}}</td>
              <td>{{b.rate}}%</td>
              <td>£{{b.taxable | number:'1.0-0'}}</td>
              <td class="rpt-tbl__val">£{{b.due | number:'1.0-0'}}</td>
            </tr>
            <tr *ngIf="additionalSurcharge > 0">
              <td class="rpt-tbl__key">Additional Dwelling Surcharge</td>
              <td>5%</td>
              <td>£{{calcData?.PropertyValue | number:'1.0-0'}}</td>
              <td class="rpt-tbl__val">£{{additionalSurcharge | number:'1.0-0'}}</td>
            </tr>
            <tr *ngIf="calcData?.ForeignBuyer==='1' && foreignSurcharge > 0">
              <td class="rpt-tbl__key">Foreign Buyer Surcharge</td>
              <td>2%</td>
              <td>£{{calcData?.PropertyValue | number:'1.0-0'}}</td>
              <td class="rpt-tbl__val">£{{foreignSurcharge | number:'1.0-0'}}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3">Total SDLT</td>
              <td class="rpt-tbl__val">£{{stampDutyLandTax | number:'1.0-0'}}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </section>

</div>

<app-report-footer
  [today]="today"
  (download)="printScreen()"
  (recalculate)="startagain()">
</app-report-footer>
